import React, { useState, useEffect, useRef } from 'react';
import { TextInput } from '@carbon/react';
import { Location } from '@carbon/icons-react';
import mapBoxGeocodingService from '../services/MapBoxGeocodingService';
import { useTheme } from '../context/ThemeContext';

const AddressAutosuggestInput = ({
  placeholder,
  value,
  onChangeText,
  onAddressSelected,
  style,
  maxLength,
  searchOnlyOnUserInput = false,
}) => {
  const { theme } = useTheme();
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const searchTimeoutRef = useRef(null);
  const userTypingRef = useRef(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  // Debounced search for address suggestions
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If restricted to user input, do not search on prefilled/programmatic values
    if (searchOnlyOnUserInput && !userTypingRef.current) {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsLoading(false);
      return;
    }

    console.log(`🔍 Address search triggered: "${value}" (length: ${value.length})`);

    if (value.length >= 3) {
      setIsLoading(true);
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          console.log(`🌐 Calling MapBox API for: "${value}"`);
          const results = await mapBoxGeocodingService.searchAddresses(value, 5);
          console.log(`✅ MapBox returned ${results.length} results:`, results);
          setSuggestions(results);
          setShowSuggestions(results.length > 0);
        } catch (error) {
          console.error('❌ Failed to search addresses:', error);
          setSuggestions([]);
          setShowSuggestions(false);
        } finally {
          setIsLoading(false);
        }
      }, 300); // 300ms delay
    } else {
      console.log(`🚫 Search skipped - input too short: "${value}"`);
      setSuggestions([]);
      setShowSuggestions(false);
      setIsLoading(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [value, searchOnlyOnUserInput]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target) &&
        !event.target.closest('#address-input')
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSuggestionPress = (suggestion) => {
    // Set the input value to the street address (house number + street if available)
    const streetDisplay = suggestion.streetAddress || suggestion.text;
    onChangeText(streetDisplay);
    
    // Hide suggestions
    setShowSuggestions(false);
    setSuggestions([]);
    userTypingRef.current = false;

    // If callback provided, pass structured data directly from suggestion
    if (onAddressSelected) {
      const result = {
        streetAddress: streetDisplay,
        city: suggestion.context.locality,
        state: suggestion.context.region,
        zipCode: suggestion.context.postcode,
        country: suggestion.context.country,
        latitude: suggestion.coordinates[1],
        longitude: suggestion.coordinates[0],
        formattedAddress: suggestion.placeName,
      };
      onAddressSelected(result);
    }
  };

  const handleTextChange = (e) => {
    const text = e.target.value;
    userTypingRef.current = true;
    onChangeText(text);
    
    // If user clears the input, hide suggestions
    if (text.length === 0) {
      setShowSuggestions(false);
      setSuggestions([]);
    }
  };

  const handleFocus = () => {
    // Do not pop suggestions on focus for prefilled values when restricted to user input
    if (searchOnlyOnUserInput) {
      return;
    }
    // Show suggestions if we have them and input has content
    if (suggestions.length > 0 && value.length >= 3) {
      setShowSuggestions(true);
    }
  };

  const handleBlur = () => {
    // Hide suggestions with a small delay to allow for suggestion tap
    setTimeout(() => {
      setShowSuggestions(false);
    }, 150);
  };

  return (
    <div style={{ position: 'relative', zIndex: 1000, ...style }}>
      <div ref={inputRef} style={{ position: 'relative' }}>
        <TextInput
          id="address-input"
          labelText=""
          placeholder={placeholder}
          value={value}
          onChange={handleTextChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          maxLength={maxLength}
          style={{ width: '100%' }}
        />
        {isLoading && (
          <div style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
          }}>
            <div style={{
              width: '16px',
              height: '16px',
              border: `2px solid ${borderColor}`,
              borderTopColor: '#0f62fe',
              borderRadius: '50%',
              animation: 'spin 0.6s linear infinite',
            }} />
          </div>
        )}
      </div>

      {/* Suggestions List */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: bgSecondary,
            border: `1px solid ${borderColor}`,
            borderRadius: '4px',
            marginTop: '4px',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 1001,
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          }}
        >
          {suggestions.map((item) => (
            <div
              key={item.id}
              onClick={() => handleSuggestionPress(item)}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                borderBottom: `1px solid ${borderColor}`,
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isDark ? '#353535' : '#e0e0e0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Location size={16} style={{ color: '#0f62fe', marginRight: '12px', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  color: textPrimary,
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '2px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {item.text}
                </div>
                <div style={{
                  color: textSecondary,
                  fontSize: '12px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {item.placeName}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AddressAutosuggestInput;

